import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

function getParam(location, key) {
  return new URLSearchParams(location.search).get(key) || "";
}

function getCurrentUrl(location) {
  return `${location.pathname}${location.search}`;
}

function getUserContext(location) {
  const { state } = location;
  const user = state?.user;
  return {
    userId: state?.userId || user?._id || getParam(location, "user_id"),
    userName: state?.userName || user?.name || getParam(location, "user_name"),
    userEmail:
      state?.userEmail || user?.email || getParam(location, "user_email"),
  };
}

function getAgentContext(location) {
  const { state } = location;
  return {
    agentId:
      state?.agentId || state?.agent?._id || getParam(location, "agent_id"),
    agentName:
      state?.agentName ||
      state?.agent?.name ||
      getParam(location, "agent_name"),
  };
}

function buildUserInfoUrl({ userId, userName, userEmail }) {
  const query = new URLSearchParams({ user_id: userId });
  if (userName) query.append("user_name", userName);
  if (userEmail) query.append("user_email", userEmail);
  return `/company-info?${query.toString()}`;
}

function buildAgentListUrl({ userId, userName }) {
  const query = new URLSearchParams({ user_id: userId });
  if (userName) query.append("user_name", userName);
  return `/agent-list?${query.toString()}`;
}

function buildAgentInfoUrl({
  agentId,
  agentName,
  userId,
  userName,
  userEmail,
  returnToAllAgents,
  fromUserInfo,
}) {
  const query = new URLSearchParams({ agent_id: agentId });
  if (agentName) query.append("agent_name", agentName);
  if (userId) query.append("user_id", userId);
  if (userName) query.append("user_name", userName);
  if (userEmail) query.append("user_email", userEmail);
  if (returnToAllAgents) query.append("from", "all");
  else if (fromUserInfo) query.append("from", "company-info");
  return `/agent-info?${query.toString()}`;
}

function getUserInfoItems(location) {
  const { userId, userName } = getUserContext(location);
  if (!userId) return [];

  return [
    { label: "Company Management", to: "/company-list" },
    { label: userName || "User Info", to: getCurrentUrl(location) },
  ];
}

function getAgentListItems(location) {
  const { userId, userName } = getUserContext(location);
  if (!userId) return [];

  return [
    { label: "Company Management", to: "/company-list" },
    {
      label: userName || "User Info",
      to: buildUserInfoUrl({ userId, userName }),
    },
    { label: "Agents", to: getCurrentUrl(location) },
  ];
}

function getAgentInfoItems(location) {
  const { state } = location;
  const { userId, userName, userEmail } = getUserContext(location);
  const { agentName } = getAgentContext(location);
  const returnToAllAgents =
    state?.returnToAllAgents ?? getParam(location, "from") === "all";
  const fromUserInfo =
    state?.fromUserInfo ?? getParam(location, "from") === "company-info";
  const currentLabel = agentName || "Agent Info";

  if (returnToAllAgents) {
    return [
      { label: "Agents", to: "/allagentlist" },
      { label: currentLabel, to: getCurrentUrl(location) },
    ];
  }

  if (!userId) return [];

  if (fromUserInfo) {
    return [
      { label: "Company Management", to: "/company-list" },
      {
        label: userName || "User Info",
        to: buildUserInfoUrl({ userId, userName, userEmail }),
      },
      { label: currentLabel, to: getCurrentUrl(location) },
    ];
  }

  return [
    { label: "Company Management", to: "/company-list" },
    {
      label: userName || "User Info",
      to: buildUserInfoUrl({ userId, userName, userEmail }),
    },
    { label: "Agents", to: buildAgentListUrl({ userId, userName }) },
    { label: currentLabel, to: getCurrentUrl(location) },
  ];
}

function getAgentCreateItems(location) {
  const { state } = location;
  const { userId, userName, userEmail } = getUserContext(location);
  const { agentId, agentName } = getAgentContext(location);
  const returnToAllAgents = !!state?.returnToAllAgents;
  const fromUserInfo = !!state?.fromUserInfo;
  const returnToAgentInfo = !!state?.returnToAgentInfo;
  const currentLabel = agentId
    ? agentName
      ? `Edit ${agentName}`
      : "Edit Agent"
    : "Create Agent";

  const agentInfoCrumb =
    returnToAgentInfo && agentId
      ? [
          {
            label: agentName || "Agent Info",
            to: buildAgentInfoUrl({
              agentId,
              agentName,
              userId,
              userName,
              userEmail,
              returnToAllAgents,
              fromUserInfo,
            }),
          },
        ]
      : [];

  if (returnToAllAgents) {
    return [
      { label: "Agents", to: "/allagentlist" },
      ...agentInfoCrumb,
      { label: currentLabel, to: getCurrentUrl(location) },
    ];
  }

  if (!userId) return [];

  if (fromUserInfo) {
    return [
      { label: "Company Management", to: "/company-list" },
      {
        label: userName || "User Info",
        to: buildUserInfoUrl({ userId, userName, userEmail }),
      },
      ...agentInfoCrumb,
      { label: currentLabel, to: getCurrentUrl(location) },
    ];
  }

  return [
    { label: "Company Management", to: "/company-list" },
    {
      label: userName || "User Info",
      to: buildUserInfoUrl({ userId, userName, userEmail }),
    },
    { label: "Agents", to: buildAgentListUrl({ userId, userName }) },
    ...agentInfoCrumb,
    { label: currentLabel, to: getCurrentUrl(location) },
  ];
}

function getAddSubscriptionItems(location) {
  const { userId, userName, userEmail } = getUserContext(location);
  if (!userId) return [];

  return [
    {
      label: userName || "User Info",
      to: buildUserInfoUrl({ userId, userName, userEmail }),
    },
    { label: "Add Subscription", to: getCurrentUrl(location) },
  ];
}

function getKnowledgeBaseListItems(location) {
  const { state } = location;
  const { userId, userName, userEmail } = getUserContext(location);
  const { agentId, agentName } = getAgentContext(location);
  const returnToAllAgents = !!state?.returnToAllAgents;

  const agentCrumb = agentId
    ? [
        {
          label: agentName || "Agent Info",
          to: buildAgentInfoUrl({
            agentId,
            agentName,
            userId,
            userName,
            userEmail,
            returnToAllAgents,
          }),
        },
      ]
    : [];

  if (returnToAllAgents) {
    return [
      { label: "Agents", to: "/allagentlist" },
      ...agentCrumb,
      { label: "Knowledge Base", to: getCurrentUrl(location) },
    ];
  }

  if (!userId) return [];

  return [
    { label: "Company Management", to: "/company-list" },
    {
      label: userName || "User Info",
      to: buildUserInfoUrl({ userId, userName, userEmail }),
    },
    { label: "Agents", to: buildAgentListUrl({ userId, userName }) },
    ...agentCrumb,
    { label: "Knowledge Base", to: getCurrentUrl(location) },
  ];
}

function getBreadcrumbItems(location) {
  switch (location.pathname) {
    case "/create-company":
      return [
        { label: "Company Accounts", to: "/company-list" },
        { label: "New", to: getCurrentUrl(location) },
      ];
    case "/company-info":
      return getUserInfoItems(location);
    case "/add-subscription":
      return getAddSubscriptionItems(location);
    case "/agent-list":
      return getAgentListItems(location);
    case "/agent-info":
      return getAgentInfoItems(location);
    case "/agent-create":
      return getAgentCreateItems(location);
    case "/agent-knowledgebase":
      return getKnowledgeBaseListItems(location);
    default:
      return [];
  }
}

export default function Breadcrumbs() {
  const location = useLocation();
  const items = getBreadcrumbItems(location);

  if (!items.length) return null;

  return (
    <nav className="app-breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={`${item.label}-${index}`} className="app-breadcrumb-item">
            {index > 0 && (
              <span className="app-breadcrumb-separator" aria-hidden="true">
                <ChevronRight size={14} strokeWidth={2} />
              </span>
            )}
            {item.to && !isLast ? (
              <Link to={item.to} className="app-breadcrumb-link">
                {item.label}
              </Link>
            ) : (
              <span className="app-breadcrumb-current">{item.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
